// Kilat 4U storefront behaviour. Loaded as a module from snippets/k4u-head.liquid.
import { CartLinesUpdateEvent } from '@shopify/events';

// The few strings this file shows come from the #k4u-i18n island (snippets/k4u-head-i18n.liquid), in the visitor's
// language. EN holds today's English so a missing island degrades to English, never to key names.
const EN = {
  'stories.dialog_label': 'Customer story',
  'actions.close': 'Close',
  'stories.previous': 'Previous',
  'stories.next': 'Next',
  'wa.opening': 'Opening WhatsApp…',
};
const island = (() => {
  try { return JSON.parse(document.getElementById('k4u-i18n')?.textContent || '{}'); } catch { return {}; }
})();
const dec = (s) => (typeof s === 'string' ? s.replace(/&#39;|&#x27;/g, "'").replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&') : s); // Liquid's t filter HTML-escapes
const tx = (key) => dec(island[key]) || EN[key];
const attr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

const json = (url, body) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((r) => r.json().then((data) => ({ ok: r.ok, data })));

// "Upgrade to machine + 3-Step Set": swap a machine line in the cart for its bundle variant,
// then let Horizon's cart components re-render through the same event the product form uses.
document.addEventListener('click', async (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-k4u-swap]') : null;
  if (!button || button.disabled) return;
  event.preventDefault();

  const { lineKey, variantId, productId } = button.dataset;
  const quantity = Number(button.dataset.quantity) || 1;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(variantId), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    // Through Theme.routes so the calls keep the language prefix (/zh-CN/cart/...) and answer in the visitor's language.
    await json(`${Theme.routes.cart_change_url}.js`, { id: lineKey, quantity: 0 });
    const added = await json(Theme.routes.cart_add_url, { items: [{ id: Number(variantId), quantity }] });
    const cart = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'k4u-swap', sourceId: 'k4u-swap', itemCount: quantity, productId, didError: !added.ok },
    });
    if (added.ok) button.dispatchEvent(new CustomEvent('k4u:added', { bubbles: true }));
  } catch (error) {
    deferred.reject(error);
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
});

// Background videos (snippets/k4u-video.liquid). The still paints first; the footage downloads only
// after the page has loaded (hero) or near the screen (the rest), plays while on screen, and is
// skipped on Save-Data / 2G / 3G and for reduced motion.
const slowConnection = () => {
  const c = navigator.connection;
  return !!c && (c.saveData || ['slow-2g', '2g', '3g'].includes(c.effectiveType));
};
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
document.querySelectorAll('[data-k4u-video]').forEach((box) => {
  const video = box.querySelector('video');
  if (!video || slowConnection()) return;
  let started = false;
  let onScreen = false;
  const play = () => {
    if (!started || !onScreen || document.hidden || reduceMotion.matches) return;
    video.play().catch(() => {});
  };
  const start = () => {
    if (started || reduceMotion.matches) return;
    started = true;
    video.muted = true;
    video.preload = 'auto';
    video.addEventListener('playing', () => box.classList.add('is-playing'), { once: true });
    play();
  };
  new IntersectionObserver(([entry]) => {
    onScreen = entry.isIntersecting;
    if (onScreen && box.dataset.k4uVideo === 'visible') start();
    if (onScreen) play(); else if (started) video.pause();
  }, { rootMargin: '200px 0px' }).observe(box);
  if (box.dataset.k4uVideo === 'load') {
    const soon = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 2000 }) : setTimeout(start, 800));
    if (document.readyState === 'complete') soon(); else window.addEventListener('load', soon, { once: true });
    ['pointerdown', 'keydown', 'scroll'].forEach((type) => window.addEventListener(type, start, { once: true, passive: true }));
  }
  reduceMotion.addEventListener?.('change', () => (reduceMotion.matches ? video.pause() : (start(), play())));
  document.addEventListener('visibilitychange', play);
});

// Story rows: thumbnails load once the row is within about a screen of view (see sections/k4u-orders.liquid).
const fillStories = (row) => row.querySelectorAll('img[data-srcset]').forEach((img) => {
  img.srcset = img.dataset.srcset; img.src = img.dataset.src;
  img.removeAttribute('data-srcset'); img.removeAttribute('data-src');
});
const storyRows = document.querySelectorAll('[data-k4u-stories]');
if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => entries.forEach((e) => {
    if (e.isIntersecting) { fillStories(e.target); io.unobserve(e.target); }
  }), { rootMargin: '700px 0px' });
  storyRows.forEach((row) => io.observe(row));
} else storyRows.forEach(fillStories);

// Customer stories: a full-screen viewer that behaves like the Instagram stories they came from.
// Tap the right side for the next one and the left side to go back, hold to pause a clip, swipe
// sideways to change, swipe down to close (it follows the finger 1:1 and keeps its speed on release).
// Arrow buttons and the keyboard work too.
const lightbox = (() => {
  let dlg, stage, bar, count, items = [], index = 0, drag = null;
  const media = () => stage.firstElementChild;
  const preload = (i) => { const t = items[(i + items.length) % items.length]; if (t && !t.dataset.video) new Image().src = t.dataset.src; };
  const render = (dir = 0) => {
    const t = items[index];
    stage.innerHTML = t.dataset.video
      ? `<video src="${t.dataset.video}" poster="${t.dataset.src}" autoplay muted loop playsinline disablepictureinpicture></video>`
      : `<img src="${t.dataset.src}" alt="${attr(t.dataset.caption || '')}">`;
    if (dir && !calm.matches) media().animate(
      [{ transform: `translateX(${dir * 36}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }],
      { duration: 300, easing: 'cubic-bezier(.16, 1, .3, 1)' });
    count.textContent = `${index + 1} / ${items.length}`;
    bar.innerHTML = items.length <= 40 ? items.map((_, i) => `<i${i <= index ? ' class="on"' : ''}></i>`).join('') : `<b style="--p:${(index + 1) / items.length}"></b>`;
    preload(index + 1); preload(index - 1);
  };
  const go = (step) => { index = (index + step + items.length) % items.length; render(step); };
  const settle = (el, to, ms = 380) => el.animate([{ transform: el.style.transform || 'none' }, { transform: to }],
    { duration: ms, easing: 'cubic-bezier(.16, 1, .3, 1)' }).finished.catch(() => {});
  const close = async (velocity = 0) => {
    const el = media();
    if (el && !calm.matches) {
      const y = parseFloat((el.style.transform.match(/translateY\((-?[\d.]+)px/) || [])[1] || 0);
      const travel = innerHeight - y;
      el.animate([{ transform: el.style.transform || 'none', opacity: 1 }, { transform: `translateY(${innerHeight}px) scale(.85)`, opacity: 0 }],
        { duration: Math.max(160, Math.min(320, travel / Math.max(velocity, 1.2))), easing: 'cubic-bezier(.4, 0, 1, 1)', fill: 'forwards' });
      dlg.animate([{ backgroundColor: getComputedStyle(dlg).backgroundColor }, { backgroundColor: 'rgba(8,9,8,0)' }], { duration: 240, fill: 'forwards' });
      await new Promise((r) => setTimeout(r, 230));
    }
    dlg.close();
  };
  const build = () => {
    dlg = document.createElement('dialog');
    dlg.className = 'k4u-lightbox';
    dlg.setAttribute('aria-label', tx('stories.dialog_label'));
    dlg.innerHTML = '<div class="k4u-lightbox__bar" aria-hidden="true"></div><span class="k4u-lightbox__count"></span>'
      + `<button type="button" class="k4u-lightbox__close" aria-label="${attr(tx('actions.close'))}">×</button>`
      + `<button type="button" class="k4u-lightbox__prev" aria-label="${attr(tx('stories.previous'))}">‹</button><button type="button" class="k4u-lightbox__next" aria-label="${attr(tx('stories.next'))}">›</button>`
      + '<div class="k4u-lightbox__stage"></div>';
    document.body.append(dlg);
    stage = dlg.querySelector('.k4u-lightbox__stage'); bar = dlg.querySelector('.k4u-lightbox__bar'); count = dlg.querySelector('.k4u-lightbox__count');
    dlg.addEventListener('click', (e) => {
      if (e.target.closest('.k4u-lightbox__close') || e.target === dlg) close();
      else if (e.target.closest('.k4u-lightbox__prev')) go(-1);
      else if (e.target.closest('.k4u-lightbox__next')) go(1);
    });
    dlg.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
    });
    stage.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || drag) return;
      stage.setPointerCapture(e.pointerId);
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, axis: null, held: false, samples: [[e.clientX, e.clientY, performance.now()]] };
      drag.hold = setTimeout(() => {
        if (!drag || drag.axis) return;
        drag.held = true;
        const v = stage.querySelector('video'); if (v) v.pause();
        stage.classList.add('is-held');
      }, 240);
    });
    stage.addEventListener('pointermove', (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      drag.samples.push([e.clientX, e.clientY, performance.now()]); if (drag.samples.length > 5) drag.samples.shift();
      if (!drag.axis && Math.hypot(dx, dy) > 10) { drag.axis = Math.abs(dy) > Math.abs(dx) ? 'y' : 'x'; clearTimeout(drag.hold); stage.classList.add('is-dragging'); }
      const el = media(); if (!el || calm.matches) return;
      if (drag.axis === 'y') {
        const down = Math.max(0, dy), up = Math.min(0, dy);
        const pull = down + up * 0.2; // a little give upwards, then resistance
        el.style.transform = `translateY(${pull}px) scale(${1 - Math.min(down / 2400, 0.12)})`;
        dlg.style.setProperty('--k4u-lb-fade', String(1 - Math.min(down / 520, 0.75)));
      } else if (drag.axis === 'x') {
        el.style.transform = `translateX(${dx * 0.9}px)`;
      }
    });
    const end = async (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      clearTimeout(drag.hold);
      const d = drag; drag = null;
      stage.classList.remove('is-dragging', 'is-held');
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      const [x0, y0, t0] = d.samples[0]; const dt = Math.max(1, performance.now() - t0);
      const vx = (e.clientX - x0) / dt, vy = (e.clientY - y0) / dt; // px per ms, from the last few moves
      const el = media();
      if (d.held) { const v = stage.querySelector('video'); if (v) v.play().catch(() => {}); return; }
      if (e.type === 'pointercancel') { if (el) { await settle(el, 'none'); el.style.transform = ''; } dlg.style.removeProperty('--k4u-lb-fade'); return; }
      if (!d.axis) { // a tap: left third goes back, the rest goes forward
        const r = stage.getBoundingClientRect();
        go((e.clientX - r.left) / r.width < 0.3 ? -1 : 1);
        return;
      }
      if (d.axis === 'y' && (dy > 120 || (vy > 0.11 && dy > 24))) { close(vy); return; }
      if (d.axis === 'x' && (Math.abs(dx) > 70 || (Math.abs(vx) > 0.3 && Math.abs(dx) > 20))) { go(dx < 0 ? 1 : -1); return; }
      if (el) { await settle(el, 'none'); el.style.transform = ''; }
      dlg.animate([{ '--k4u-lb-fade': dlg.style.getPropertyValue('--k4u-lb-fade') || '1' }, { '--k4u-lb-fade': '1' }], { duration: 200 });
      dlg.style.removeProperty('--k4u-lb-fade');
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
    dlg.addEventListener('close', () => { stage.innerHTML = ''; dlg.getAnimations().forEach((a) => a.cancel()); dlg.style.removeProperty('--k4u-lb-fade'); });
  };
  return (trigger) => {
    if (!dlg) build();
    const scope = trigger.closest('[data-k4u-stories]') || document;
    items = Array.from(scope.querySelectorAll('[data-k4u-lightbox]'));
    index = Math.max(0, items.indexOf(trigger));
    render();
    dlg.showModal();
  };
})();
document.addEventListener('click', (e) => {
  const trigger = e.target instanceof Element ? e.target.closest('[data-k4u-lightbox]') : null;
  if (trigger) { e.preventDefault(); lightbox(trigger); return; }
  const arrow = e.target instanceof Element ? e.target.closest('.k4u-stories__arrow') : null;
  if (arrow) {
    const list = arrow.closest('[data-k4u-stories]').querySelector('.k4u-stories__list');
    list.scrollBy({ left: Number(arrow.dataset.dir) * list.clientWidth * 0.85, behavior: 'smooth' });
  }
});

// ---------------------------------------------------------------------------------------------
// Feedback that answers the visitor. See "The label at full strength" in assets/k4u.css.
// ---------------------------------------------------------------------------------------------
const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

// iOS Safari only runs :active press styles when the page listens for touches.
document.addEventListener('touchstart', () => {}, { passive: true });

// The press foams from where the thumb lands: a soft circle spreads inside the button (assets/k4u.css).
document.addEventListener('pointerdown', (e) => {
  if (calm.matches || !(e.target instanceof Element)) return;
  const b = e.target.closest('.k4u-btn, .button:not(.button-unstyled), .add-to-cart-button, .k4u-wa-float');
  if (!b || b.disabled || b.getAttribute('aria-disabled') === 'true') return;
  const r = b.getBoundingClientRect();
  b.style.setProperty('--rx', `${e.clientX - r.left}px`);
  b.style.setProperty('--ry', `${e.clientY - r.top}px`);
  b.classList.remove('k4u-ripple');
  void b.offsetWidth; // restart on a quick second tap
  b.classList.add('k4u-ripple');
}, { passive: true });
document.addEventListener('animationend', (e) => {
  if (e.animationName === 'k4u-ripple' && e.target instanceof Element) e.target.classList.remove('k4u-ripple');
});

// Add to cart foams: soap bubbles rise off the button that was pressed, and when the cart drawer
// slides in, a smaller burst rises from that product's row, so the foam follows it into the cart.
// Horizon marks the button data-added="true" the moment the add shows on screen: that's the cue.
const foamAt = (el, { count, host } = {}) => {
  if (calm.matches || !el?.isConnected) return;
  const r = el.getBoundingClientRect();
  if (!r.width) return;
  const box = host || el.closest('dialog[open]') || document.body;
  const o = box === document.body ? { left: -scrollX, top: -scrollY } : box.getBoundingClientRect();
  const layer = document.createElement('div');
  layer.className = 'k4u-foam';
  layer.setAttribute('aria-hidden', 'true');
  layer.style.cssText = `left:${r.left - o.left}px;top:${r.top - o.top}px;width:${r.width}px;height:${r.height}px`;
  box.append(layer);
  const n = count || Math.min(20, 9 + Math.round(r.width / 20));
  let left = n;
  for (let i = 0; i < n; i += 1) {
    const b = document.createElement('span');
    const size = 10 + Math.random() * 20;
    b.style.cssText = `width:${size}px;height:${size}px;left:${8 + Math.random() * 84}%;top:${30 + Math.random() * 40}%`;
    layer.append(b);
    const dx = (Math.random() - 0.5) * 80;
    const dy = -(40 + Math.random() * 95);
    b.animate([
      { transform: 'translate(-50%, -50%) scale(.2)', opacity: 0 },
      { transform: `translate(calc(-50% + ${dx * 0.3}px), calc(-50% + ${dy * 0.3}px)) scale(1)`, opacity: 1, offset: 0.25 },
      { transform: `translate(calc(-50% + ${dx * 0.8}px), calc(-50% + ${dy * 0.8}px)) scale(1.06)`, opacity: 0.9, offset: 0.78 },
      { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(1.35)`, opacity: 0 },
    ], { duration: 900 + Math.random() * 600, delay: Math.random() * 160, easing: 'cubic-bezier(.16, 1, .3, 1)', fill: 'both' })
      .finished.catch(() => {}).then(() => { left -= 1; if (!left) layer.remove(); });
  }
};
// Free delivery from RM100: the bar pours to its new level, and fizzes the one time it fills.
let lastPct = document.querySelector('.k4u-ship__bar') ? Number(document.querySelector('.k4u-ship__bar').dataset.pct) : null;
const shipLevel = (bar, before) => {
  const now = Number(bar.dataset.pct);
  if (now >= 100 && before < 100) foamAt(bar, { count: 16 });
  lastPct = now;
};
let lastAdd = null;
const addedFrom = (origin, source) => {
  foamAt(origin);
  const form = source.closest('form');
  lastAdd = { variant: form?.querySelector('input[name="id"]')?.value || source.dataset.variantId || '', at: performance.now() };
  const cart = document.querySelector('.header-actions__cart-icon');
  if (cart) {
    cart.classList.remove('k4u-cart-bump');
    void cart.offsetWidth; // restart the bump on quick repeat adds
    cart.classList.add('k4u-cart-bump');
  }
};
// The sticky bar's button submits the main form, whose button may be scrolled out of sight:
// the foam comes off whichever one the finger actually pressed.
let pressed = null;
let pressedAt = 0;
document.addEventListener('pointerdown', (e) => {
  const b = e.target instanceof Element ? e.target.closest('.add-to-cart-button, [data-k4u-swap]') : null;
  if (b) { pressed = b; pressedAt = performance.now(); }
}, { passive: true, capture: true });
const onScreen = (el) => { const r = el.getBoundingClientRect(); return r.width > 0 && r.bottom > 0 && r.top < innerHeight; };
new MutationObserver((records) => {
  for (const record of records) {
    const el = record.target;
    if (!(el instanceof HTMLElement)) continue;
    if (record.attributeName === 'data-added') {
      if (el.dataset.added !== 'true' || record.oldValue === 'true') continue;
      const recent = pressed && performance.now() - pressedAt < 4000 && pressed.isConnected && onScreen(pressed);
      addedFrom(recent && !onScreen(el) ? pressed : el, el);
    } else if (el instanceof HTMLDialogElement && el.open && record.oldValue === null && el.closest('#cart-drawer')) {
      if (!lastAdd || performance.now() - lastAdd.at > 3000) continue;
      const { variant } = lastAdd;
      lastAdd = null;
      let tries = 0;
      const find = () => {
        const row = (variant && el.querySelector(`.cart-items__table-row[data-key^="${variant}:"]`))
          || el.querySelector('.cart-items__table-row.adding');
        if (row) foamAt(row.querySelector('.cart-items__media') || row, { count: 10, host: el });
        else if ((tries += 1) < 6) setTimeout(find, 250);
      };
      setTimeout(find, 380); // after the drawer has slid in
    }
    if (record.attributeName === 'data-pct') shipLevel(el, Number(record.oldValue));
  }
  // Horizon may swap the cart's markup instead of patching it: pour from the last level we saw.
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      const bar = node.matches('.k4u-ship__bar') ? node : node.querySelector('.k4u-ship__bar');
      if (bar && lastPct !== null && Number(bar.dataset.pct) !== lastPct) {
        const fill = bar.querySelector('span');
        const to = bar.dataset.pct;
        fill.style.setProperty('--pct', String(lastPct));
        void fill.offsetWidth;
        fill.style.setProperty('--pct', to);
        shipLevel(bar, lastPct);
      } else if (bar) lastPct = Number(bar.dataset.pct);
    }
  }
}).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-added', 'open', 'data-pct'], attributeOldValue: true });
document.addEventListener('k4u:added', (e) => e.target instanceof HTMLElement && addedFrom(e.target, e.target));

// Things that play once when they come into view: the wash timeline, the 1 : 10 strips.
// Anything already on screen at load is left as it is, so nothing blinks.
const plays = document.querySelectorAll('.k4u-timeline, [data-k4u-play]');
if (plays.length && 'IntersectionObserver' in window && !calm.matches) {
  const io = new IntersectionObserver((entries) => entries.forEach((entry) => {
    if (!entry.isIntersecting) return;
    io.unobserve(entry.target);
    requestAnimationFrame(() => entry.target.classList.remove('k4u-pre'));
  }), { threshold: 0.35 });
  plays.forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top < innerHeight && r.bottom > 0) return;
    el.classList.add('k4u-pre');
    io.observe(el);
  });
}

// Where did this customer come from? An ad link that carries ?ref=<name> (or utm_content, utm_source, or a fbclid) is remembered for 30 days (last touch),
// and every WhatsApp order message that is opened afterwards ends with a line "Ref: fb/p6-gauge", so the owner sees which ad produced the order.
(() => {
  try {
    const q = new URLSearchParams(location.search);
    const c = q.get('ref') || q.get('utm_content') || '';
    const src = q.get('utm_source') || (q.get('fbclid') ? 'fb' : '');
    if (c || src) localStorage.setItem('k4u_ref', JSON.stringify({ s: src.slice(0, 12), c: c.slice(0, 40), t: Date.now() }));
  } catch { /* storage blocked */ }
})();
const refLine = () => {
  try {
    const r = JSON.parse(localStorage.getItem('k4u_ref') || 'null');
    if (!r || Date.now() - r.t > 30 * 864e5) return '';
    return [r.s, r.c].filter(Boolean).join('/').replace(/[^\w\-./]/g, '');
  } catch { return ''; }
};

// WhatsApp answers back: the tap is acknowledged before the app opens. The label never says "sent":
// the site can't know that.
document.addEventListener('click', (e) => {
  const a = e.target instanceof Element ? e.target.closest('a[href*="wa.me/"], a[href*="api.whatsapp.com"]') : null;
  if (a && a.href.includes('text=') && !a.href.includes('Ref%3A')) {
    const ref = refLine();
    if (ref) a.href += '%0A' + encodeURIComponent('Ref: ' + ref);
  }
  if (!a || a.classList.contains('k4u-wa-opening')) return;
  navigator.vibrate?.(10);
  a.classList.add('k4u-wa-opening');
  const label = a.matches('.k4u-wa-float') ? a.querySelector('span')
    : a.matches('.k4u-btn') ? [...a.childNodes].reverse().find((n) => n.nodeType === 3 && n.textContent.trim()) : null;
  const before = label?.textContent;
  if (label) {
    if (a.matches('.k4u-btn')) a.style.minWidth = `${a.offsetWidth}px`;
    label.textContent = tx('wa.opening');
  }
  setTimeout(() => {
    a.classList.remove('k4u-wa-opening');
    if (label) { label.textContent = before; a.style.minWidth = ''; }
  }, 1600);
});

// Product page demo video: the card opens a full-screen player. The video is only requested on the tap
// (the card itself loads a 4 KB thumbnail), and it plays with sound because the tap is the permission.
const demoPlayer = (() => {
  let dlg, video;
  const build = (t) => {
    dlg = document.createElement('dialog');
    dlg.className = 'k4u-demo-dlg';
    dlg.innerHTML = `<button type="button" class="k4u-demo-dlg__close" aria-label="${attr(t.dataset.close || '')}">×</button>`
      + '<video controls playsinline preload="none" disablepictureinpicture controlslist="nodownload"></video>';
    document.body.append(dlg);
    video = dlg.querySelector('video');
    dlg.addEventListener('click', (e) => { if (e.target === dlg || e.target.closest('.k4u-demo-dlg__close')) dlg.close(); });
    dlg.addEventListener('close', () => { video.pause(); video.removeAttribute('src'); video.removeAttribute('poster'); video.load(); window.k4uLoops?.resume(); });
    video.addEventListener('play', () => { if (!dlg.dataset.tracked) { dlg.dataset.tracked = '1'; window.fbq?.('trackCustom', 'PlayDemo', { content_name: dlg.dataset.product }); } }, { once: true });
  };
  return (t) => {
    if (!dlg) build(t);
    dlg.setAttribute('aria-label', t.dataset.label || '');
    dlg.dataset.product = t.dataset.product || '';
    delete dlg.dataset.tracked;
    window.k4uLoops?.stop();
    video.poster = t.dataset.poster;
    video.src = t.dataset.video;
    dlg.showModal();
    video.play().catch(() => {});
  };
})();
// capture phase: the photo gallery opens its zoom view on a tap, and the demo slide must not
document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target.closest('[data-k4u-demo]') : null;
  if (t) { e.preventDefault(); e.stopImmediatePropagation(); demoPlayer(t); }
}, true);

// Demo strip on machine pages: a short silent loop starts when the strip is near the screen, plays while it is on screen and
// pauses when it leaves. Skipped on data-saver, slow connections and reduced motion: the still and the play button stay.
(() => {
  const vids = [...document.querySelectorAll('[data-k4u-loop]')];
  if (!vids.length) return;
  const slow = calm.matches || slowConnection() || matchMedia('(max-width: 749px)').matches;   // phones: the poster and the play button, nothing downloads before the tap
  const inView = (v) => { const r = v.getBoundingClientRect(); return r.bottom > innerHeight * 0.2 && r.top < innerHeight * 0.8; };
  if (!slow) vids.forEach((v) => {
    new IntersectionObserver(([en]) => {
      if (!en.isIntersecting || v.getAttribute('src')) return;
      v.src = v.dataset.k4uLoop;
      v.addEventListener('playing', () => v.classList.add('is-on'), { once: true });
    }, { rootMargin: '500px 0px' }).observe(v);
    new IntersectionObserver(([en]) => { if (!v.getAttribute('src')) return; if (en.isIntersecting) v.play().catch(() => {}); else v.pause(); }, { threshold: 0.35 }).observe(v);
  });
  // the full video takes over while its player is open
  window.k4uLoops = { stop: () => vids.forEach((v) => v.pause()), resume: () => vids.forEach((v) => { if (v.getAttribute('src') && inView(v)) v.play().catch(() => {}); }) };
})();


// Scroll reveal (Apple style): anything with data-k4u-reveal rises and fades in once, when it comes near the screen.
(() => {
  const els = document.querySelectorAll('[data-k4u-reveal]');
  if (!els.length) return;
  if (calm.matches || !('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return; }
  const io = new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -6% 0px', threshold: 0.06 });
  els.forEach((e) => io.observe(e));
})();
